import { uiLocale } from '../i18n';

const PARIS = 'Europe/Paris';
const formatters = new Map();

const formatter = (kind, options) => {
  const locale = uiLocale();
  const key = `${locale}:${kind}:${JSON.stringify(options)}`;
  if (!formatters.has(key)) {
    formatters.set(key, kind === 'date' ? new Intl.DateTimeFormat(locale, options) : new Intl.NumberFormat(locale, options));
  }
  return formatters.get(key);
};

export const EMPTY = '—';

const isMissing = (value) => value === null || value === undefined || value === '' || !Number.isFinite(Number(value));

export const formatMoney = (value, { decimals = 2, compact = false, sign = false, currency = 'EUR' } = {}) => {
  if (isMissing(value)) return EMPTY;
  return formatter('number', {
    style: 'currency',
    currency,
    minimumFractionDigits: compact ? 0 : decimals,
    maximumFractionDigits: compact ? 1 : decimals,
    ...(compact ? { notation: 'compact' } : {}),
    ...(sign ? { signDisplay: 'exceptZero' } : {}),
  }).format(Number(value));
};

export const formatPercent = (value, { decimals = 1, sign = true } = {}) => {
  if (isMissing(value)) return EMPTY;
  return formatter('number', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    ...(sign ? { signDisplay: 'exceptZero' } : {}),
  }).format(Number(value) / 100);
};

export const formatNumber = (value, { decimals = 0, sign = false } = {}) => {
  if (isMissing(value)) return EMPTY;
  return formatter('number', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    ...(sign ? { signDisplay: 'exceptZero' } : {}),
  }).format(Number(value));
};

const DATE_STYLES = {
  short: { day: 'numeric', month: 'short', year: 'numeric' },
  long: { day: 'numeric', month: 'long', year: 'numeric' },
  full: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
  dayMonth: { day: 'numeric', month: 'short' },
  month: { month: 'short' },
  monthYear: { month: 'long', year: 'numeric' },
};

const toDate = (value) => {
  if (value instanceof Date) return { date: value, calendar: false };
  const text = String(value || '');
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return { date: new Date(`${text}T00:00:00Z`), calendar: true };
  return { date: new Date(text), calendar: false };
};

export const formatDate = (value, style = 'short') => {
  if (!value) return EMPTY;
  const { date, calendar } = toDate(value);
  if (Number.isNaN(date.getTime())) return EMPTY;
  return formatter('date', { ...(DATE_STYLES[style] || DATE_STYLES.short), timeZone: calendar ? 'UTC' : PARIS }).format(date);
};

export const formatDateTime = (value) => {
  if (!value) return EMPTY;
  const { date } = toDate(value);
  if (Number.isNaN(date.getTime())) return EMPTY;
  return formatter('date', { dateStyle: 'medium', timeStyle: 'short', timeZone: PARIS }).format(date);
};

export const formatTime = (value) => {
  if (!value) return EMPTY;
  const { date } = toDate(value);
  if (Number.isNaN(date.getTime())) return EMPTY;
  return formatter('date', { hour: '2-digit', minute: '2-digit', timeZone: PARIS }).format(date);
};

export const toneOf = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return 'neutral';
  return n > 0 ? 'gain' : 'loss';
};
